import React, {forwardRef} from 'react';
import classnames from 'classnames';
import {format, parse} from 'date-fns';
import DatePicker, {ReactDatePickerProps} from 'react-datepicker';
import en from 'date-fns/locale/en-GB';
import he from 'date-fns/locale/he';
import {useSelector} from 'react-redux';
import {useIntl} from 'react-intl';
import ButtonAction from '../ButtonAction';
import {RootState} from '../../../../store/configureStore';
import icon from '../../../assets/icons/cancel-circle.svg';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './InputDateRange.module.scss';

export interface InputDateRangeProps extends ReactDatePickerProps<any, true> {
  id?: string;
  onReset?: () => void,
}

export interface CustomInputProps {
  currentLocale: string,
  value?: string,
  placeholder?: string,
  className?: string,
  onClick?: () => void,
  disabled?: boolean,
  onReset?: () => void,
}

export type Ref = HTMLButtonElement;


const CustomInput = forwardRef<Ref, CustomInputProps>(({
                                                         value,
                                                         placeholder,
                                                         className,
                                                         onClick,
                                                         disabled,
                                                         currentLocale,
                                                         onReset
                                                       }, ref) => {
  const intl = useIntl();
  const datepickerUntil = intl.formatMessage({
    id: 'datepicker-until',
    defaultMessage: 'until'
  });

  const getInputValue = () => {
    const datesArray = Boolean(value) ? value?.split(' - ') : [];

    if (datesArray) {
      const [fromDateStr, toDateStr] = datesArray;
      const fromDate = parse(fromDateStr, 'dd/MM/yyyy', new Date());
      const toDate = parse(toDateStr, 'dd/MM/yyyy', new Date());
      const separatedFromDate = Boolean(datesArray[0]) ? datesArray[0].split('/') : '';
      const separatedToDate = Boolean(datesArray[1]) ? datesArray[1].split('/') : '';

      const formattedFromDate = Boolean(separatedFromDate)
        ? format(
          fromDate,
          'dd MMM yy',
          {locale: (currentLocale === 'he') ? he : en}
        )
        : '';
      const formattedToDate = Boolean(separatedToDate)
        ? format(
          toDate,
          'dd MMM yy',
          {locale: (currentLocale === 'he') ? he : en}
        )
        : '';

      if (value) {
        return (
          <div className={styles.customValue}>
            <span className={styles.fromDate}>{formattedFromDate}</span>
            <span className={styles.until}>{datepickerUntil}</span>
            <span className={styles.toDate}>{formattedToDate}</span>
          </div>
        );
      } else {
        return placeholder;
      }
    }
  };

  const handleReset = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (onReset) {
      onReset();
    }
  };

  return (
    <div
      className={classnames(
        {[styles.disabled]: disabled},
        className
      )}
      onClick={onClick}
      {...ref}
    >
          <span className={classnames({[styles.placeholder]: Boolean(!value)})}>
            {getInputValue()}
          </span>
      {(onReset) && (
        <ButtonAction
          variant={'icon'}
          icon={icon}
          onClick={handleReset}
          customClassnames={{container: styles.btnReset}}
        />
      )}
    </div>);
});

const InputDateRange = ({startDate, endDate, onChange, onReset}: InputDateRangeProps) => {
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const intl = useIntl();
  const placeholderText = intl.formatMessage({
    id: 'datepicker-placeholder',
    defaultMessage: 'Date'
  });

  return (
    <DatePicker
      selected={startDate}
      onChange={onChange}
      startDate={startDate}
      endDate={endDate}
      selectsRange
      dateFormat="dd/MM/yyyy"
      locale={(currentLocale === 'he') ? he : en}
      placeholderText={placeholderText}
      wrapperClassName={styles.wrapperDatepicker}
      popperClassName={styles.popper}
      className={styles.datepicker}
      customInput={(
        <CustomInput
          currentLocale={currentLocale}
          onReset={onReset}
        />
      )}
    />
  );
};

export default InputDateRange;
